import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-ink/55 backdrop-blur-[4px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      // Prevent overlay from blocking touch events on content
      "touch-none",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
  }
>(({ className, children, hideCloseButton, onOpenAutoFocus, ...props }, ref) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  /*
    Radix moves focus to the first focusable child when a dialog opens. On a
    phone that child is usually a text input, so iOS raises the keyboard and
    scrolls the VISUAL viewport to the field while the dialog is still
    animating in. Because the scroll lock sets `overflow: hidden` on the body,
    there is nothing left to pan, and the dialog reads as frozen — reported on
    Chrome for iOS opening the customer "My bookings" form.

    Below sm we keep focus on the panel itself: the dialog is still announced,
    Escape and the focus trap still work, and the keyboard appears only when
    the user actually taps the field. Desktop keeps the original behaviour,
    where focusing the first input is the right thing.
  */
  const handleOpenAutoFocus = (event: Event) => {
    onOpenAutoFocus?.(event);
    if (event.defaultPrevented) return;

    const isCompact =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 639px)').matches;

    if (!isCompact) return;

    event.preventDefault();
    contentRef.current?.focus({ preventScroll: true });
  };

  return (
  <DialogPortal>
    <DialogOverlay />
    {/*
      Positioning layer, sized with `100dvh` rather than positioned with
      `fixed bottom-0`.

      Mobile browsers resolve `position: fixed` and `vh` against the LARGE
      viewport — the one that assumes the URL bar and toolbars are hidden. A
      sheet pinned with `bottom-0` therefore ends up underneath the browser
      chrome (and underneath the software keyboard), which is what cropped
      this dialog on iOS Safari and Chrome Android. `dvh` tracks the visible
      viewport instead, so the sheet's bottom edge stays where the user can
      see and tap it, and the content scrolls internally when space is tight.
    */}
    <div className="pointer-events-none fixed inset-0 z-50 flex h-[100dvh] items-end justify-center sm:items-center sm:p-4">
      <DialogPrimitive.Content
        ref={(node) => {
          contentRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        tabIndex={-1}
        onOpenAutoFocus={handleOpenAutoFocus}
        className={cn(
          "pointer-events-auto relative grid w-full max-h-full gap-4 bg-surface p-6 shadow-modal duration-200",
          "rounded-t-2xl pb-[calc(1.5rem+env(safe-area-inset-bottom))]",
          "sm:max-w-[440px] sm:rounded-4xl sm:pb-6",
          "overflow-y-auto overscroll-contain",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          "sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0",
          "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",
          className
        )}
        style={{
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y pinch-zoom',
        }}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-3 top-3 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-soft transition-colors hover:bg-surface-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:pointer-events-none active:scale-95">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </div>
  </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-row gap-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-tight tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

/**
 * DialogHero — DESIGN_SYSTEM.md §2.1
 * Full-bleed colored band at the top of a Dialog. Used by the confirmation
 * pattern (success gradient + check icon).
 */
type DialogHeroTone = 'brand' | 'success' | 'danger' | 'warn';

const heroToneClass: Record<DialogHeroTone, string> = {
  brand:   'bg-gradient-to-br from-brand-500 to-brand-700 text-white',
  success: 'bg-gradient-to-br from-success to-success-text text-white',
  danger:  'bg-gradient-to-br from-danger to-danger-text text-white',
  warn:    'bg-gradient-to-br from-warn to-warn-text text-white',
};

const DialogHero = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { tone?: DialogHeroTone }
>(({ className, tone = 'success', ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      '-mx-6 -mt-6 flex flex-col items-center gap-3 px-6 py-8 sm:rounded-t-4xl',
      heroToneClass[tone],
      className,
    )}
    {...props}
  />
));
DialogHero.displayName = 'DialogHero';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogHero,
};